import { useMemo } from "react"
import { useQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { GetSuggestedFulfilmentsDocument } from "@hydrologiq/gql-experience/graphql"
import { type SuggestedFulfilment, type SuggestedFulfilmentsResponse } from "./siteGroupingUtils"

/**
 * Hook to fetch suggested fulfilments for multiple sites.
 * Uses Apollo's cache-first policy to avoid unnecessary refetches.
 * Only refetches when explicitly requested via refetch().
 */
export function useSuggestedFulfilments(
  siteIds: string[],
  loadedDateRange: { start: string; end: string } | null,
  fallbackStartDate: number,
  enabled: boolean
): { 
  suggestions: SuggestedFulfilment[]
  siteResponses: Map<string, SuggestedFulfilmentsResponse>
  loading: boolean
  refetch: () => void
} {
  // Derive stable query dates from loadedDateRange or fallback to a 6-month window
  const queryDates = useMemo(() => {
    if (loadedDateRange) {
      return {
        start_date: new Date(loadedDateRange.start).toISOString(),
        end_date: new Date(loadedDateRange.end).toISOString(),
      }
    }
    // Fallback: use a wide range from fallbackStartDate
    const start = new Date(fallbackStartDate)
    const end = new Date(start)
    end.setMonth(end.getMonth() + 6)
    return {
      start_date: start.toISOString(),
      end_date: end.toISOString(),
    }
  }, [loadedDateRange?.start, loadedDateRange?.end, fallbackStartDate])

  const { data, loading, refetch } = useQueryWithEndpoint(
    GetSuggestedFulfilmentsDocument,
    {
      variables: {
        site_ids: siteIds,
        start_date: queryDates.start_date,
        end_date: queryDates.end_date,
      },
      skip: !enabled || siteIds.length === 0,
      fetchPolicy: "cache-first",
    },
    "primary"
  )

  const { suggestions, siteResponses } = useMemo(() => {
    if (!data?.getSuggestedFulfilments) {
      return { suggestions: [], siteResponses: new Map() }
    }

    const responses = data.getSuggestedFulfilments as SuggestedFulfilmentsResponse[]
    const allSuggestions: SuggestedFulfilment[] = []
    const responseMap = new Map<string, SuggestedFulfilmentsResponse>()

    for (const response of responses) {
      allSuggestions.push(...response.suggestions)
      responseMap.set(response.site_id, response)
    }

    return { suggestions: allSuggestions, siteResponses: responseMap }
  }, [data])

  return { 
    suggestions, 
    siteResponses, 
    loading, 
    refetch
  }
}
